/*
================================================================================
| FILENAME: src/features/home/HomeView.jsx (Updated for Branded Icons)
| PURPOSE:  To apply the same custom branding (colors and icons) from the
|           InsightsView to the Competitor News section on the dashboard.
|
| KEY CHANGES:
| 1.  Imported new icons from `lucide-react` (Landmark, Group, Workflow, etc.)
|     to match the product-specific branding.
| 2.  The `categoryStyles` object has been expanded with the custom product
|     styles, ensuring visual consistency with the InsightsView.
| 3.  Added a hidden div containing all dynamic Tailwind CSS classes to prevent
|     them from being purged during the production build process.
================================================================================
*/
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Users, Lightbulb, Plus, Loader, AlertTriangle, RefreshCw, ChevronRight, Database, Link, BarChartHorizontal, ChevronLeft, ExternalLink,
    Landmark, Group, Workflow, Cloud, Sheet, Code // New icons for custom product styles
} from 'lucide-react';
import apiService from '../../services/apiService';

// --- STYLING & CONFIGURATION ---
const categoryStyles = {
    // --- NEW: Custom styles for your specific products ---
    "SplashBI for Financial Analytics": { color: "emerald", icon: <Landmark size={14} /> },
    "SplashBI for People Analytics": { color: "purple", icon: <Group size={14} /> },
    "Data Pipeline": { color: "orange", icon: <Workflow size={14} /> },
    "SplashBI for Oracle Fusion Cloud": { color: "red", icon: <Cloud size={14} /> },
    "GL Connect": { color: "green", icon: <Sheet size={14} /> },
    "SQL Connect": { color: "sky", icon: <Code size={14} /> },

    // --- Original default styles ---
    "People Analytics": { color: "sky", icon: <Users size={14} /> },
    "default": { color: "slate", icon: <BarChartHorizontal size={14} /> }
};

const getCategoryClasses = (category) => {
    const style = categoryStyles[category] || categoryStyles.default;
    return {
        textColor: `text-${style.color}-400`,
        bgColor: `bg-${style.color}-500/10`,
        ringColor: `group-hover:ring-${style.color}-500/50`,
    };
};

const useInsights = (fetchFunction) => {
    const [allItems, setAllItems] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadData = useCallback(async (forceRefresh = false) => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await fetchFunction(15, forceRefresh);
            const key = Object.keys(data)[0];
            const itemsFromServer = data[key] || [];
            setAllItems(itemsFromServer);
            setTotalCount(itemsFromServer.length);
        } catch (err) {
            setError('Failed to load insights. Please try again.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [fetchFunction]);

    useEffect(() => {
        loadData(false);
    }, [loadData]);

    const refresh = () => {
        loadData(true);
    };

    return { allItems, totalCount, isLoading, error, refresh };
};

// --- Calendar and Campaign components ---
const Card = ({ children, className = '' }) => ( <div className={`bg-slate-900/60 border border-slate-800 rounded-lg shadow-lg ${className}`}>{children}</div> );

const CalendarAndCampaigns = ({ tasks, campaigns }) => {
    const [currentDate, setCurrentDate] = useState(new Date());

    const changeMonth = (amount) => { setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + amount, 1)); };

    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const filteredTasks = useMemo(() => tasks.filter(task => {
        if (!task.date) return false;
        const taskDate = new Date(task.date);
        return taskDate.getFullYear() === year && taskDate.getMonth() === month;
    }), [tasks, year, month]);

    const campaignMap = useMemo(() => new Map(campaigns.map(c => [c._id, c])), [campaigns]);

    const getDynamicCampaignStatus = (campaign) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const startDate = new Date(campaign.startDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + (campaign.duration * 7));

        if (endDate < today) return { text: 'Complete', color: 'green' };
        if (startDate > today) return { text: 'Scheduled', color: 'purple' };
        if (startDate <= today && endDate >= today && campaign.status === 'Active') {
            return { text: 'In-Progress', color: 'brand' };
        }
        return { text: campaign.status, color: 'slate' };
    };
    
    const onViewTask = (task) => {
        console.log("Viewing task:", task);
    };

    return (
        <div className="flex flex-col gap-6">
             <Card className="p-4">
                <div className="flex justify-between items-center mb-4">
                    <button onClick={() => changeMonth(-1)} className="p-2 rounded-md hover:bg-slate-700"><ChevronLeft /></button>
                    <h2 className="text-lg font-bold text-white">{currentDate.toLocaleString('default', { month: 'long' })} {year}</h2>
                    <button onClick={() => changeMonth(1)} className="p-2 rounded-md hover:bg-slate-700"><ChevronRight /></button>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center text-xs text-slate-400 mb-2">{['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => <div key={day}>{day}</div>)}</div>
                <div className="grid grid-cols-7 gap-1">
                    {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} className="rounded-md"></div>)}
                    {Array.from({ length: daysInMonth }).map((_, day) => {
                        const dayNumber = day + 1;
                        const tasksForDay = filteredTasks.filter(t => new Date(t.date).getUTCDate() === dayNumber);
                        return (
                            <div key={day} className="h-20 p-1 bg-slate-800/20 rounded-md flex flex-col overflow-hidden">
                                <span className="font-semibold text-slate-300 text-xs">{dayNumber}</span>
                                <div className="space-y-1 mt-1 overflow-y-auto pr-1">
                                    {tasksForDay.map(task => {
                                        const campaign = campaignMap.get(task.campaignId);
                                        const color = campaign ? (getDynamicCampaignStatus(campaign).color) : 'gray';
                                        return (
                                            <button key={task._id} onClick={() => onViewTask(task)} className={`w-full text-left p-1 rounded text-[10px] leading-tight text-white bg-${color}-600/30 border-l-2 border-${color}-400`}>
                                                {task.title}
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Card>
            <Card className="p-4">
                <h3 className="text-lg font-bold text-white mb-4">Active Campaigns</h3>
                <div className="space-y-3">
                    {campaigns.map(camp => {
                        const dynamicStatus = getDynamicCampaignStatus(camp);
                        return (
                            <div key={camp._id} className={`w-full p-3 rounded-lg text-left transition-colors bg-slate-700/50`}>
                                <h4 className="font-bold text-white text-sm">{camp.name}</h4>
                                <p className={`text-xs font-semibold text-${dynamicStatus.color}-400`}>{dynamicStatus.text}</p>
                            </div>
                        )
                    })}
                </div>
            </Card>
        </div>
    );
};


const DashboardSection = ({ title, fetchFunction, cardType, onCreateTask, onNavigate }) => {
    const { allItems, totalCount, isLoading, error, refresh } = useInsights(fetchFunction);
    
    const displayItems = allItems.slice(0, 3);

    const handleAddToFlightDeck = (item) => {
        const taskData = { title: item.title, description: item.description, status: 'Backlog', priority: 'Medium', origin: title };
        if (onCreateTask) onCreateTask(taskData);
        else console.error("onCreateTask prop was not provided.");
    };

    const renderCard = (item, index) => {
        if (cardType === 'competitor') {
            const { textColor, bgColor, ringColor } = getCategoryClasses(item.category);
            const style = categoryStyles[item.category] || categoryStyles.default;

            return (
                <div key={index} className={`group p-4 rounded-lg transition-all duration-300 hover:bg-slate-800/50 relative ring-1 ring-transparent ${ringColor}`}>
                    <div className="flex justify-between items-start">
                        <div className={`flex items-center gap-2 text-xs font-bold mb-3 ${textColor}`}>
                            <span className={`p-1.5 rounded-full ${bgColor}`}>
                                {style.icon}
                            </span>
                            <span>{item.category || 'General'}</span>
                        </div>
                        <button
                            onClick={() => handleAddToFlightDeck(item)}
                            className="text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity hover:text-white"
                        >
                            <Plus size={18} />
                        </button>
                    </div>

                    <h3 className="font-semibold text-slate-100">{item.title}</h3>
                    <p className="text-sm text-slate-400 mt-1 line-clamp-2">{item.description}</p>
                    
                    {item.sourceUrl && item.sourceUrl !== '#' && (
                         <div className="mt-3 pt-3 border-t border-slate-800">
                             <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-400 hover:text-brand-300 transition-colors">
                                View Source <ExternalLink size={14} />
                            </a>
                        </div>
                    )}
                </div>
            );
        }

        if (cardType === 'idea') {
            return (
                <div key={index} className="group p-4 rounded-lg transition-colors duration-200 hover:bg-slate-800/50 relative">
                    <p className="text-xs font-bold text-purple-400 bg-purple-500/10 px-2 py-1 rounded-full inline-block mb-3">{item.tag || "New Content"}</p>
                    <h3 className="font-semibold text-slate-100">{item.title}</h3>
                    <p className="text-sm text-slate-400 mt-1">{item.description}</p>
                     <button
                        onClick={() => handleAddToFlightDeck(item)}
                        className="absolute top-4 right-4 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity hover:text-white"
                    >
                        <Plus size={18} />
                    </button>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="bg-slate-900/60 border border-slate-800 rounded-lg shadow-lg flex flex-col">
            <header className="flex justify-between items-center p-4 border-b border-slate-800">
                <h2 className="text-lg font-bold text-white">{title} <span className="ml-2 text-sm font-medium bg-slate-700 text-slate-300 px-2.5 py-1 rounded-full">{totalCount}</span></h2>
                <div className="flex items-center gap-4">
                    <button onClick={refresh} disabled={isLoading} className="flex items-center gap-1.5 text-sm font-medium text-slate-400 hover:text-white transition-colors disabled:opacity-50">
                        <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} /> Refresh
                    </button>
                    <button onClick={onNavigate} className="flex items-center gap-1.5 text-sm font-medium text-slate-400 hover:text-white transition-colors">
                        View More <ChevronRight size={14} />
                    </button>
                </div>
            </header>

            <div className="p-2 flex-grow">
                 {isLoading && <div className="text-center text-slate-400 py-10"><Loader className="animate-spin inline-block" /></div>}
                 {error && <div className="text-center text-red-400 py-10"><AlertTriangle className="inline-block mr-2" /> {error}</div>}
                 {!isLoading && !error && (
                    <div className="space-y-2">
                        {displayItems.map(renderCard)}
                    </div>
                 )}
            </div>
        </div>
    )
}

const HomeView = ({ user, onCreateTask, setActiveView }) => {
    const [tasks, setTasks] = useState([]);
    const [campaigns, setCampaigns] = useState([]);

    useEffect(() => {
        const fetchCalendarData = async () => {
            try {
                const [tasksData, campaignsData] = await Promise.all([
                    apiService.fetchFlightDeckTasks(),
                    apiService.fetchCampaigns()
                ]);
                setTasks(tasksData || []);
                setCampaigns(campaignsData || []);
            } catch (error) {
                console.error("Failed to fetch calendar data for dashboard:", error);
            }
        };
        fetchCalendarData();
    }, []);

    const navigateToInsights = (tab) => {
        if (setActiveView) {
            setActiveView('insights', { tab });
        } else {
            console.error("setActiveView prop is missing. Navigation failed.");
        }
    };

    return (
        <div className="p-4 sm:p-6 md:p-8 animate-fade-in min-h-full">
            {/* This hidden div ensures Tailwind's JIT compiler includes the dynamic classes we need for the custom product styles */}
            <div className="hidden text-emerald-400 bg-emerald-500/10 group-hover:ring-emerald-500/50 text-purple-400 bg-purple-500/10 group-hover:ring-purple-500/50 text-orange-400 bg-orange-500/10 group-hover:ring-orange-500/50 text-red-400 bg-red-500/10 group-hover:ring-red-500/50 text-green-400 bg-green-500/10 group-hover:ring-green-500/50 text-sky-400 bg-sky-500/10 group-hover:ring-sky-500/50 text-slate-400 bg-slate-500/10 group-hover:ring-slate-500/50 text-brand-400 bg-brand-600/30 border-brand-400"></div>

            <header className="pb-5 mb-8">
                <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">Dashboard</h1>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

                <div className="lg:col-span-2 flex flex-col gap-8">
                    <DashboardSection
                        title="Competitor News"
                        fetchFunction={apiService.fetchCompetitorWatchFeed}
                        cardType="competitor"
                        onCreateTask={onCreateTask}
                        onNavigate={() => navigateToInsights('competitor')}
                    />
                    <DashboardSection
                        title="Campaign Ideas"
                        fetchFunction={apiService.fetchNewIdeas}
                        cardType="idea"
                        onCreateTask={onCreateTask}
                        onNavigate={() => navigateToInsights('idea')}
                    />
                </div>

                <div className="lg:col-span-1 flex flex-col gap-8 sticky top-8">
                    <CalendarAndCampaigns tasks={tasks} campaigns={campaigns} />
                </div>
            </div>
        </div>
    );
};

export default HomeView;